import { Component } from '@theme/component';

/**
 * Carousel of the cards of "Dónde se usa en México" (`sections/pc-industrias.liquid`).
 *
 * The cards are a horizontal list with scroll snapping, so without this script the visitor can still swipe or scroll through
 * them. The script adds the buttons, one dot per group of cards (as many cards as fit on the screen move together) and the
 * automatic change. The automatic change only runs while it is allowed to move: motion is not reduced, the page is visible,
 * the carousel is on screen, the pointer is not over it, the focus is not inside it and the visitor has not paused it with the
 * control. It also starts again from the full wait after any manual move.
 *
 * Attributes of the element: `data-autoplay="true"` turns the automatic change on, `data-interval` is the wait in milliseconds
 * and `data-label-*` carry the translated texts of the controls (`[current]` and `[total]` are replaced in `data-label-dot`).
 *
 * @typedef {object} Refs
 * @property {HTMLElement} track - The scrolling list of cards.
 * @property {HTMLElement} [controls] - The bar with the dots and the buttons; hidden until the script runs.
 * @property {HTMLElement} [dots] - Holds one button per group of cards; built here.
 * @property {HTMLButtonElement} [toggle] - Pauses or resumes the automatic change.
 *
 * @extends {Component<Refs>}
 */
class PcIndustriesCarousel extends Component {
  requiredRefs = ['track'];

  /** @type {AbortController | undefined} */
  #abort;

  /** @type {IntersectionObserver | undefined} */
  #intersection;

  /** @type {ResizeObserver | undefined} */
  #resize;

  /** @type {MediaQueryList | undefined} */
  #reducedMotion;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #timer;

  #frame = 0;

  #onScreen = false;

  #hovered = false;

  #focused = false;

  /** Set when the visitor pauses the automatic change with the control. */
  #pausedByUser = false;

  #perView = 1;

  #pages = 1;

  #page = 0;

  #photosLoaded = false;

  connectedCallback() {
    super.connectedCallback();

    this.#abort = new AbortController();
    const { signal } = this.#abort;
    const { track } = this.refs;

    // The style sheet hides the scrollbar only once the buttons and dots exist
    this.dataset.ready = 'true';

    track.addEventListener('scroll', this.#onScroll, { passive: true, signal });
    this.addEventListener('pointerenter', this.#onPointerEnter, { signal });
    this.addEventListener('pointerleave', this.#onPointerLeave, { signal });
    this.addEventListener('pointerdown', this.#restart, { passive: true, signal });
    this.addEventListener('wheel', this.#restart, { passive: true, signal });
    this.addEventListener('keydown', this.#restart, { signal });
    this.addEventListener('focusin', this.#onFocusIn, { signal });
    this.addEventListener('focusout', this.#onFocusOut, { signal });
    document.addEventListener('visibilitychange', this.#sync, { signal });

    this.#reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.#reducedMotion.addEventListener('change', this.#sync, { signal });

    this.#intersection = new IntersectionObserver(this.#onIntersect, { threshold: 0.2 });
    this.#intersection.observe(this);

    this.#resize = new ResizeObserver(() => this.#layout());
    this.#resize.observe(track);

    this.#layout();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abort?.abort();
    this.#abort = undefined;
    this.#intersection?.disconnect();
    this.#intersection = undefined;
    this.#resize?.disconnect();
    this.#resize = undefined;
    clearTimeout(this.#timer);
    this.#timer = undefined;
    cancelAnimationFrame(this.#frame);
  }

  updatedCallback() {
    super.updatedCallback();

    // A section render brings the server markup back: the bar is hidden again and the dots are gone
    this.#layout();
  }

  /** Shows the previous group of cards, or the last one from the first. Called by the previous button. */
  previous() {
    this.#goTo(this.#page - 1);
  }

  /** Shows the next group of cards, or the first one from the last. Called by the next button. */
  next() {
    this.#goTo(this.#page + 1);
  }

  /**
   * Shows the group of the clicked dot. Called by the bar of dots.
   * @param {Event} event
   */
  selectPage(event) {
    const dot = event.target instanceof Element ? event.target.closest('[data-page]') : null;
    if (dot instanceof HTMLElement && dot.dataset.page !== undefined) this.#goTo(Number(dot.dataset.page));
  }

  /** Pauses or resumes the automatic change. Called by the control. */
  toggleAutoplay() {
    this.#pausedByUser = !this.#pausedByUser;
    this.#sync();
  }

  /** Measures how many cards fit, rebuilds the dots when the number of groups changes and refreshes the state. */
  #layout() {
    const { track, controls } = this.refs;
    const items = [...track.children];
    if (!items.length) return;

    const style = getComputedStyle(track);
    const gap = parseFloat(style.columnGap) || 0;
    const inner = track.clientWidth - (parseFloat(style.paddingLeft) || 0) - (parseFloat(style.paddingRight) || 0);
    const itemWidth = items[0].getBoundingClientRect().width;

    this.#perView = itemWidth > 0 ? Math.max(1, Math.round((inner + gap) / (itemWidth + gap))) : 1;
    const pages = Math.max(1, Math.ceil(items.length / this.#perView));

    if (pages !== this.#pages || !this.refs.dots?.childElementCount) {
      this.#pages = pages;
      this.#buildDots();
    }

    if (controls) controls.hidden = pages < 2;
    this.#update();
    this.#sync();
  }

  /** One button per group of cards, labelled "Group n of N". */
  #buildDots() {
    const { dots } = this.refs;
    if (!dots) return;

    const template = this.dataset.labelDot ?? '[current] / [total]';
    dots.replaceChildren(
      ...Array.from({ length: this.#pages }, (_, index) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'pc-industries__dot';
        dot.dataset.page = String(index);
        dot.setAttribute(
          'aria-label',
          template.replace('[current]', String(index + 1)).replace('[total]', String(this.#pages))
        );
        return dot;
      })
    );
  }

  /** @returns {number} Distance from the start edge of the list to the start edge of a card (negative: before it). */
  #startOffset(/** @type {Element} */ item) {
    const { track } = this.refs;
    const style = getComputedStyle(track);
    const trackRect = track.getBoundingClientRect();
    const rect = item.getBoundingClientRect();

    return style.direction === 'rtl'
      ? trackRect.right - (parseFloat(style.paddingRight) || 0) - rect.right
      : rect.left - trackRect.left - (parseFloat(style.paddingLeft) || 0);
  }

  /**
   * Scrolls to a group of cards; before the first goes to the last and after the last goes to the first.
   * @param {number} page
   */
  #goTo(page) {
    const { track } = this.refs;
    const items = [...track.children];
    if (!items.length) return;

    const target = (page + this.#pages) % this.#pages;
    // The last group may hold fewer cards than fit: it is aligned so the last card ends at the edge
    const index = Math.min(target * this.#perView, Math.max(0, items.length - this.#perView));
    const delta = this.#startOffset(items[index]);
    const rtl = getComputedStyle(track).direction === 'rtl';

    track.scrollBy({ left: rtl ? -delta : delta, behavior: this.#reducedMotion?.matches ? 'auto' : 'smooth' });
    this.#restart();
  }

  #onScroll = () => {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => this.#update());
  };

  /** Finds the group on screen and reflects it in the dots. */
  #update() {
    const { track, dots } = this.refs;
    const items = [...track.children];
    const first = items.findIndex((item) => this.#startOffset(item) >= -2);
    this.#page = Math.min(this.#pages - 1, Math.round(Math.max(0, first) / this.#perView));

    if (!dots) return;
    for (const dot of dots.children) {
      const current = dot instanceof HTMLElement && Number(dot.dataset.page) === this.#page;
      if (current) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    }
  }

  /** @param {IntersectionObserverEntry[]} entries */
  #onIntersect = (entries) => {
    this.#onScreen = entries.some((entry) => entry.isIntersecting);

    // The cards past the first group are loaded as soon as the carousel is near, so they are ready when it moves
    if (this.#onScreen && !this.#photosLoaded) {
      this.#photosLoaded = true;
      for (const photo of this.refs.track.querySelectorAll('img[loading="lazy"]')) {
        if (photo instanceof HTMLImageElement) photo.loading = 'eager';
      }
    }

    this.#sync();
  };

  /** @param {PointerEvent} event */
  #onPointerEnter = (event) => {
    if (event.pointerType !== 'mouse') return;
    this.#hovered = true;
    this.#sync();
  };

  #onPointerLeave = () => {
    this.#hovered = false;
    this.#sync();
  };

  #onFocusIn = () => {
    this.#focused = true;
    this.#sync();
  };

  /** @param {FocusEvent} event */
  #onFocusOut = (event) => {
    this.#focused = event.relatedTarget instanceof Node && this.contains(event.relatedTarget);
    this.#sync();
  };

  /** Starts the wait again from the full interval. */
  #restart = () => {
    this.#schedule();
  };

  /** Whether the automatic change is on for this carousel at all (setting, motion preference, more than one group). */
  get #autoplayEnabled() {
    return this.dataset.autoplay === 'true' && !this.#reducedMotion?.matches && this.#pages > 1;
  }

  /** Whether the automatic change may move right now. */
  get #canRun() {
    return (
      this.#autoplayEnabled &&
      !this.#pausedByUser &&
      this.#onScreen &&
      !document.hidden &&
      !this.#hovered &&
      !this.#focused
    );
  }

  /** Starts or stops the timer and reflects the state in the control and in the list. */
  #sync = () => {
    const { toggle, track } = this.refs;

    if (toggle) {
      toggle.hidden = !this.#autoplayEnabled;
      toggle.setAttribute('aria-pressed', String(this.#pausedByUser));
      toggle.setAttribute(
        'aria-label',
        (this.#pausedByUser ? toggle.dataset.labelPlay : toggle.dataset.labelPause) ?? ''
      );
    }

    // While the cards change by themselves the list is silent for screen readers; otherwise it announces the move
    track.setAttribute('aria-live', this.#autoplayEnabled && !this.#pausedByUser ? 'off' : 'polite');

    this.#schedule();
  };

  /** Arms the timer for the next change, or clears it when the carousel must stand still. */
  #schedule() {
    clearTimeout(this.#timer);
    this.#timer = undefined;
    if (!this.#canRun) return;

    const interval = Math.max(2000, Number(this.dataset.interval) || 5000);
    this.#timer = setTimeout(() => this.#goTo(this.#page + 1), interval);
  }
}

if (!customElements.get('pc-industries-carousel')) {
  customElements.define('pc-industries-carousel', PcIndustriesCarousel);
}
